import { Card, CardContent } from "@/components/ui/card";
import { Users, Building, DollarSign } from "lucide-react";
import { formatCurrency } from "@/lib/utils";
import { BeaconFundRequest } from "@/lib/api/fund";
import { LoadingIndicator } from "./LoadingIndicator";

interface StatsData {
  employeeTotal: number;
  customerTotal: number;
  employeeMatched: number;
  customerMatched: number;
  totalAllocated: number;
  totalRequested: number;
  totalMatched: number;
  totalDisbursed: number;
}

interface TotalStatsCardsProps {
  allStats: StatsData;
  fundRequests: BeaconFundRequest[];
  selectedYears: number[];
  isSummaryLoading: boolean;
}

export function TotalStatsCards({ allStats, fundRequests, selectedYears, isSummaryLoading }: TotalStatsCardsProps) {
  return (
    <div className="flex flex-col">
      <p className="font-bold">Total Stats <LoadingIndicator isLoading={isSummaryLoading} /></p>
      <div className="grid grid-cols-6 gap-3 mb-4">
        <Card>
          <CardContent className="p-3">
            <div className="flex flex-col gap-1">
              <p className="text-xs font-medium text-muted-foreground">Total Allocated</p>
              <p className="text-xl font-bold">{formatCurrency(allStats.totalAllocated)}</p>
              <p className="text-xs text-muted-foreground">
                {selectedYears.length || "All"} year{selectedYears.length !== 1 ? "s" : ""}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3">
            <div className="flex flex-col gap-1">
              <p className="text-xs font-medium text-muted-foreground">Total Requested</p>
              <p className="text-xl font-bold">{formatCurrency(allStats.totalRequested)}</p>
              <p className="text-xs text-muted-foreground">
                {selectedYears.length || "All"} year{selectedYears.length !== 1 ? "s" : ""}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-3">
            <div className="flex flex-col gap-1">
              <p className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                <Users className="w-3 h-3" />Employee
              </p>
              <p className="text-xl font-bold">{formatCurrency(allStats.employeeTotal)}</p>
              <p className="text-xs text-muted-foreground">
                Matched: {formatCurrency(allStats.employeeMatched)}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-3">
            <div className="flex flex-col gap-1">
              <p className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                <Building className="w-3 h-3" />Customer
              </p>
              <p className="text-xl font-bold">{formatCurrency(allStats.customerTotal)}</p>
              <p className="text-xs text-muted-foreground">
                Matched: {formatCurrency(allStats.customerMatched)}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-3">
            <div className="flex flex-col gap-1">
              <p className="text-xs font-medium text-muted-foreground">Total Matched</p>
              <p className="text-xl font-bold">{formatCurrency(allStats.totalMatched)}</p>
              <p className="text-xs text-muted-foreground">
                {allStats.totalRequested > 0 ? ((allStats.totalMatched / allStats.totalRequested) * 100).toFixed(1) : 0}% match rate
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-3">
            <div className="flex flex-col gap-1">
              <p className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                <DollarSign className="w-3 h-3" />Disbursed
              </p>
              <p className="text-xl font-bold">{formatCurrency(allStats.totalDisbursed)}</p>
              <p className="text-xs text-muted-foreground">
                {fundRequests.filter((r: BeaconFundRequest) => r.has_been_dispersed).length} requests
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

interface YearStatsCardsProps {
  yearStats: StatsData;
  fundRequests: BeaconFundRequest[];
  selectedYears: number[];
  isSummaryLoading: boolean;
}

export function YearStatsCards({ yearStats, fundRequests, selectedYears, isSummaryLoading }: YearStatsCardsProps) {
  if (selectedYears.length === 0) return null;

  return (
    <div className="flex flex-col">
      <p className="font-bold">Selected Year Stats <LoadingIndicator isLoading={isSummaryLoading} /></p>
      <div className="grid grid-cols-6 gap-3 mb-4">
        <Card>
          <CardContent className="p-3">
            <div className="flex flex-col gap-1">
              <p className="text-xs font-medium text-muted-foreground">Total Allocated</p>
              <p className="text-xl font-bold">{formatCurrency(yearStats.totalAllocated)}</p>
              <p className="text-xs text-muted-foreground">
                {selectedYears.length || "All"} year{selectedYears.length !== 1 ? "s" : ""}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3">
            <div className="flex flex-col gap-1">
              <p className="text-xs font-medium text-muted-foreground">Total Requested</p>
              <p className="text-xl font-bold">{formatCurrency(yearStats.totalRequested)}</p>
              <p className="text-xs text-muted-foreground">
                {selectedYears.length || "All"} year{selectedYears.length !== 1 ? "s" : ""}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-3">
            <div className="flex flex-col gap-1">
              <p className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                <Users className="w-3 h-3" />Employee
              </p>
              <p className="text-xl font-bold">{formatCurrency(yearStats.employeeTotal)}</p>
              <p className="text-xs text-muted-foreground">
                Matched: {formatCurrency(yearStats.employeeMatched)}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-3">
            <div className="flex flex-col gap-1">
              <p className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                <Building className="w-3 h-3" />Customer
              </p>
              <p className="text-xl font-bold">{formatCurrency(yearStats.customerTotal)}</p>
              <p className="text-xs text-muted-foreground">
                Matched: {formatCurrency(yearStats.customerMatched)}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-3">
            <div className="flex flex-col gap-1">
              <p className="text-xs font-medium text-muted-foreground">Total Matched</p>
              <p className="text-xl font-bold">{formatCurrency(yearStats.totalMatched)}</p>
              <p className="text-xs text-muted-foreground">
                {yearStats.totalRequested > 0 ? ((yearStats.totalMatched / yearStats.totalRequested) * 100).toFixed(1) : 0}% match rate
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-3">
            <div className="flex flex-col gap-1">
              <p className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                <DollarSign className="w-3 h-3" />Disbursed
              </p>
              <p className="text-xl font-bold">{formatCurrency(yearStats.totalDisbursed)}</p>
              <p className="text-xs text-muted-foreground">
                {fundRequests.filter((r: BeaconFundRequest) => r.has_been_dispersed).length} requests
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}